/**
 * React Query bindings for the VAU context switch.
 *
 * Switching identity requires a **full document reload**, not a
 * React-Router pseudo-navigation. The chat surface is rendered by
 * the federated `OpalChat` module (Module Federation), which keeps
 * its own in-memory store of threads, current draft, message buffer,
 * websocket subscriptions, etc. A React `key` change unmounts/remounts
 * the component shell but the module's globals survive — meaning
 * the previous caller's threads continue to be shown until the JS
 * runtime is torn down. Only `window.location.assign` reliably
 * wipes that state.
 *
 * The "graceful" feel comes from the busy scrim in
 * `VauSwitchButton.tsx` that we paint while `mutation.isPending` is
 * true — it covers the page until the new document finishes
 * loading. Net effect: a single, intentional reload after the
 * backend has acknowledged the switch.
 *
 * Flow:
 *   1. Backend `/vau/{id}/assume` (or `/release`) lands — Redis
 *      assume map is now set/cleared.
 *   2. `window.location.assign('/instances/{id}/chat')` — full
 *      reload onto a surface that's valid under either identity
 *      (the VAUs page is filtered out of the nav while assumed).
 */

import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useInstanceId } from '@frontend/hooks';

import { assumeVAU, getAssumedContext, releaseVAU } from '../services/vau-context';
import type { ReleaseResponse, VAUContextResponse } from '../types/vauContext';

const KEY = ['vau-context'] as const;

export const useVAUContext = () => {
  return useQuery<VAUContextResponse>({
    queryKey: KEY,
    queryFn: getAssumedContext,
    staleTime: 60_000,
  });
};

function useHardReloadAfterSwitch() {
  const { instanceId } = useInstanceId();
  const queryClient = useQueryClient();
  return (targetUrl?: string) => {
    // Drop any persisted identity-bound caches BEFORE the navigation
    // fires. Without this, React Query's persisted cache rehydrates the
    // human's `current-user` profile (and personal-context space lookups)
    // post-reload under the same key — so identity-sensitive surfaces
    // would still see the pre-switch data until natural staleness kicked
    // in. The matching queries below are scoped narrowly enough to
    // survive future refactors that change other parts of the key shape.
    queryClient.removeQueries({ queryKey: ['current-user'] });
    queryClient.removeQueries({ queryKey: ['auth-status'] });
    queryClient.removeQueries({ queryKey: ['self-connections'] });
    queryClient.removeQueries({ queryKey: ['vau-context'] });
    queryClient.removeQueries({ queryKey: ['vau', 'personal-context-space'] });
    queryClient.removeQueries({ queryKey: ['opal:memories'] });
    queryClient.removeQueries({ queryKey: ['opal:user-instructions'] });
    // Best-effort cleanup of the React Query localStorage persister before
    // navigation. The async persister uses a 1s throttle, so a queued
    // setItem could in theory still fire — but removing the cache key here
    // prevents the common case of stale identity data rehydrating post-reload.
    localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');

    // Default: reload back onto the current instance's chat surface. Callers
    // that need to navigate elsewhere after the switch (e.g. the global-bar
    // switch guard sending the user to a different instance / product) can
    // pass an explicit target URL and still get the same cache-purge before
    // the navigation fires.
    const target = targetUrl ?? (instanceId ? `/instances/${instanceId}/chat` : '/');
    // Full document reload — tears down the federated chat module's
    // in-memory store so threads/draft/messages all re-fetch under
    // the new caller. The VauSwitchButton's `mutation.isPending` scrim
    // is still painted while the navigation kicks off, so the user
    // sees a covered transition rather than a flash.
    window.location.assign(target);
  };
}

export const useAssumeVAU = () => {
  const reload = useHardReloadAfterSwitch();
  return useMutation({
    mutationFn: (vauId: string) => assumeVAU(vauId),
    onSuccess: () => reload(),
    onError: err => {
      toaster.create(`Could not assume VAU: ${(err as Error).message}`, { type: 'danger' });
    },
  });
};

export type ReleaseVAUOptions = {
  /**
   * Where to send the user after the release succeeds. Defaults to the
   * current instance's chat surface — matches the "Switch back" affordance
   * in the left-nav / assumed banner. The global-bar switch guard passes an
   * explicit target so the user lands on the workspace they picked, not the
   * one they're leaving.
   */
  targetUrl?: string;
  /**
   * Opt out of the built-in "Could not release VAU" error toast. The
   * global-bar switch guard sets this because it surfaces its own,
   * more-actionable "Could not switch back to your profile" message —
   * without opting out here, both would stack on a failed release. Default
   * callers (left-nav / assumed banner) omit it and keep the built-in toast.
   */
  suppressErrorToast?: boolean;
};

// TVariables is `ReleaseVAUOptions` (all fields optional); callers that
// want the current-instance default pass `{}` (or omit the field), callers
// that want an explicit destination — like the global-bar switch guard —
// pass `{ targetUrl }`.
export const useReleaseVAU = () => {
  const reload = useHardReloadAfterSwitch();
  return useMutation<ReleaseResponse, Error, ReleaseVAUOptions>({
    mutationFn: () => releaseVAU(),
    // Reload (with the identity-cache purge) runs in onSuccess so the target
    // URL rides in via the mutation `variables` — no extra per-call closure
    // needed. The page unloads here, so onSettled etc. don't run meaningfully.
    onSuccess: (_data, variables) => reload(variables.targetUrl),
    onError: (err, variables) => {
      if (variables.suppressErrorToast) return;
      toaster.create(`Could not release VAU: ${(err as Error).message}`, { type: 'danger' });
    },
  });
};
